import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { useAuthStore } from './auth'
import { useIncidentStore } from './incident'
import { useSubjectsStore } from './subjects'
import { IncidentStatus } from '@/types'
import type { MissionType, WizardChannel } from '@/types'

interface WizardStep1Data {
  mission_name: string
  mission_number: string
  op_period_hours: number
  mission_type: MissionType | null
  start_date: string
  start_time: string
  requesting_agency_id: string | null
  requesting_agency_name: string | null
}

interface WizardStep2Data {
  subject_first_name: string
  subject_last_name: string
  age: number | null
  subject_category: string
  mobility: string
  lkp_lat: number | null
  lkp_lng: number | null
  terrain_types: string[]
  situation_summary: string
}

interface WizardStep3Data {
  objectives: string[]
}

interface WizardStep4Data {
  channels: WizardChannel[]
  icp_location: string
  icp_phone: string
}

function todayStr(): string {
  return new Date().toLocaleDateString('en-CA') // YYYY-MM-DD
}

function nowTimeStr(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}


function defaultStep1(): WizardStep1Data {
  return {
    mission_name: '',
    mission_number: '',
    op_period_hours: 24,
    mission_type: null,
    start_date: todayStr(),
    start_time: nowTimeStr(),
    requesting_agency_id: null,
    requesting_agency_name: null,
  }
}

function defaultStep2(): WizardStep2Data {
  return {
    subject_first_name: '',
    subject_last_name: '',
    age: null,
    subject_category: '',
    mobility: '',
    lkp_lat: null,
    lkp_lng: null,
    terrain_types: [],
    situation_summary: '',
  }
}

function defaultStep3(): WizardStep3Data {
  return { objectives: [] }
}

function defaultStep4(): WizardStep4Data {
  return {
    channels: [],
    icp_location: '',
    icp_phone: '',
  }
}

export const useWizardStore = defineStore('wizard', () => {
  const initialized = ref(false)
  const currentStep = ref<number | 'review'>(1)
  const step1 = ref<WizardStep1Data>(defaultStep1())
  const step2 = ref<WizardStep2Data>(defaultStep2())
  const step3 = ref<WizardStep3Data>(defaultStep3())
  const step4 = ref<WizardStep4Data>(defaultStep4())
  const generatingNumber = ref(false)
  const launching = ref(false)

  const step1Valid = computed(() => step1.value.mission_name.trim() !== '')

  const step2HasData = computed(
    () =>
      step2.value.subject_first_name.trim() !== '' ||
      step2.value.subject_last_name.trim() !== '',
  )

  function init(prefill?: {
    mission_name?: string
    mission_type?: MissionType | null
    start_date?: string
    start_time?: string
  }) {
    step1.value = defaultStep1()
    step2.value = defaultStep2()
    step3.value = defaultStep3()
    step4.value = defaultStep4()
    currentStep.value = 1
    if (prefill) {
      if (prefill.mission_name) step1.value.mission_name = prefill.mission_name
      if (prefill.mission_type) step1.value.mission_type = prefill.mission_type
      if (prefill.start_date) step1.value.start_date = prefill.start_date
      if (prefill.start_time) step1.value.start_time = prefill.start_time
    }
    initialized.value = true
  }

  async function generateMissionNumber() {
    const auth = useAuthStore()
    const agencyId = auth.userProfile?.agency_id
    if (!agencyId) return

    generatingNumber.value = true
    try {
      const agencyRef = doc(db, 'agencies', agencyId)
      let newNumber = 1

      await runTransaction(db, async (transaction) => {
        const agencySnap = await transaction.get(agencyRef)
        const lastNumber = agencySnap.exists()
          ? (agencySnap.data().last_incident_number ?? 0)
          : 0
        newNumber = (lastNumber as number) + 1
        transaction.update(agencyRef, { last_incident_number: newNumber })
      })

      const year = new Date().getFullYear().toString().slice(-2)
      const padded = String(newNumber).padStart(3, '0')
      step1.value.mission_number = `${year}-${padded}`
    } finally {
      generatingNumber.value = false
    }
  }

  async function launch(
    status: IncidentStatus.Active | IncidentStatus.Draft,
  ): Promise<string> {
    const auth = useAuthStore()
    const incidentStore = useIncidentStore()
    const subjectsStore = useSubjectsStore()
    const sync = useSyncStore()

    launching.value = true
    sync.pendingWrites++
    try {
      const s1 = step1.value
      const { incidentId, opId } = await incidentStore.createIncident({
        incident_name: s1.mission_name.trim(),
        incident_number: s1.mission_number.trim() || null,
        contact_phone: null,
        agency_id: auth.userProfile!.agency_id,
        created_by: auth.currentUser!.uid,
        mission_type: s1.mission_type,
        start_date: s1.start_date,
        start_time: s1.start_time,
        requesting_agency_id: s1.requesting_agency_id,
        requesting_agency_name: s1.requesting_agency_name,
        situation_summary: step2.value.situation_summary.trim() || null,
        status,
      })

      // Write ICP location and phone to OP 1
      const icpLocation = step4.value.icp_location.trim() || null
      const icpPhone = step4.value.icp_phone.trim() || null
      if (icpLocation || icpPhone) {
        await updateDoc(
          doc(db, 'incidents', incidentId, 'operational_periods', opId),
          {
            ...(icpLocation !== null && { icp_location: icpLocation }),
            ...(icpPhone !== null && { contact_phone: icpPhone }),
          },
        )
      }

      // Step 2 — create subject if any name was entered
      if (step2HasData.value) {
        const s2 = step2.value
        await subjectsStore.createSubject(incidentId, {
          first_name: s2.subject_first_name.trim(),
          last_name: s2.subject_last_name.trim(),
          age: s2.age,
          subject_category: s2.subject_category,
          mobility: s2.mobility,
          terrain_types: s2.terrain_types,
          // required Subject fields with defaults
          gender: '',
          physical_description: '',
          medical_conditions: '',
          medications: '',
          experience_level: '',
          behavior_if_lost: '',
          survival_training: '',
          lkp_lat: s2.lkp_lat,
          lkp_lng: s2.lkp_lng,
          lkp_time: null,
          lkp_witness: '',
          intended_dest: '',
          planned_route: '',
          expected_return: '',
          overdue_by: '',
          vehicle_description: '',
          vehicle_location: '',
          companions: '',
          companion_names: '',
          date_reported: null,
          time_reported: null,
          height: '',
          weight: '',
          race: '',
          complexion: '',
          hair_color: '',
          eye_color: '',
          clothing_description: '',
          photo_url: null,
          risk_category: null,
          reporter_name: '',
          reporter_relationship: '',
          reporter_phone: '',
          how_reported: '',
        })
      }

      // Step 3 — write objectives
      const objectives = step3.value.objectives
      for (let i = 0; i < objectives.length; i++) {
        await addDoc(
          collection(
            db,
            'incidents',
            incidentId,
            'operational_periods',
            opId,
            'objectives',
          ),
          {
            text: objectives[i],
            order: i,
            created_at: serverTimestamp(),
          },
        )
      }

      // Step 4 — write included comms channels
      const includedChannels = step4.value.channels.filter((c) => c.include)
      for (let i = 0; i < includedChannels.length; i++) {
        const ch = includedChannels[i]!
        await addDoc(
          collection(
            db,
            'incidents',
            incidentId,
            'operational_periods',
            opId,
            'comms_plan',
          ),
          {
            name: ch.name,
            assignment: ch.assignment,
            rx_freq: ch.rx_freq,
            rx_tone: ch.rx_tone,
            tx_freq: ch.tx_freq,
            tx_tone: ch.tx_tone,
            mode: ch.mode,
            remarks: ch.remarks,
            order: i,
            created_at: serverTimestamp(),
          },
        )
      }

      return incidentId
    } finally {
      launching.value = false
      sync.pendingWrites--
    }
  }

  function reset() {
    initialized.value = false
    currentStep.value = 1
    step1.value = defaultStep1()
    step2.value = defaultStep2()
    step3.value = defaultStep3()
    step4.value = defaultStep4()
  }

  return {
    initialized,
    currentStep,
    step1,
    step2,
    step3,
    step4,
    generatingNumber,
    launching,
    step1Valid,
    step2HasData,
    init,
    generateMissionNumber,
    launch,
    reset,
  }
})
